import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Box,
    Typography,
    alpha,
    useTheme
} from '@mui/material';
import { MdWarning, MdInfo, MdQuestionMark } from 'react-icons/md';

const ConfirmationDialog = ({
    open,
    title,
    content,
    onConfirm,
    onCancel,
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmColor = "primary",
    severity = "info"
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const getIcon = () => {
        switch (severity) {
            case 'warning': return <MdWarning size={24} color={theme.palette.warning.main} />;
            case 'error': return <MdWarning size={24} color={theme.palette.error.main} />;
            case 'info': return <MdInfo size={24} color={theme.palette.info.main} />;
            default: return <MdQuestionMark size={24} color={theme.palette.primary.main} />;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    p: 1.5,
                    bgcolor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'white',
                    backgroundImage: 'none',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    boxShadow: isDark ? '0 25px 60px rgba(0,0,0,0.5)' : '0 15px 40px rgba(0,0,0,0.1)',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
                <Box sx={{
                    p: 1,
                    borderRadius: 2,
                    display: 'flex',
                    bgcolor: alpha(theme.palette[confirmColor === 'warning' ? 'warning' : 'primary'].main, 0.1)
                }}>
                    {getIcon()}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    lineHeight: 1.6
                }}>
                    {content}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={onCancel}
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 3,
                        borderRadius: 2
                    }}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color={confirmColor}
                    autoFocus
                    sx={{
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 4,
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none'
                        }
                    }}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
